import { useAuthentication } from '@psyomics/ui-common';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Header,
  Illustration,
} from '@psyomics/ui-components';
import { ReactNode, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

const CRISIS_ACKNOWLEDGED_KEY = 'crisisAcknowledged';

const CrisisAcknowledgement = ({ isOpen, onConfirm }: { readonly isOpen: boolean; readonly onConfirm: () => void }) => {
  const { t } = useTranslation();

  return (
    <Dialog open={isOpen}>
      <DialogContent
        className="bg-indigo-300"
        data-testid="crisis-dialog"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        role="alertdialog"
        showCloseButton={false}
      >
        <div className="flex flex-col justify-center items-center gap-6 py-4">
          <Illustration className="shrink-0" name="Heart1" size={135} />
          <div className="w-full flex flex-col items-start gap-6">
            <DialogTitle asChild className="font-normal" noStyle>
              <Header className="font-normal" level="h1">
                {t('auth.login.crisisCheck.title')}
              </Header>
            </DialogTitle>
            <DialogDescription className="text-foreground text-lg">
              <Trans i18nKey={'auth.login.crisisCheck.description'} />
            </DialogDescription>
            <Button appearance="primary" data-testid="crisis-confirm-button" onClick={onConfirm}>
              {t('common.buttons.continue')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CrisisAcknowledgementGate = ({ children }: { readonly children: ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuthentication();
  const [acknowledged, setAcknowledged] = useState(() => localStorage.getItem(CRISIS_ACKNOWLEDGED_KEY) === 'true');

  useEffect(() => {
    if (isAuthenticated) {
      setAcknowledged(localStorage.getItem(CRISIS_ACKNOWLEDGED_KEY) === 'true');
    } else if (!isLoading) {
      localStorage.removeItem(CRISIS_ACKNOWLEDGED_KEY);
      setAcknowledged(false);
    }
  }, [isAuthenticated, isLoading]);

  const handleConfirm = () => {
    localStorage.setItem(CRISIS_ACKNOWLEDGED_KEY, 'true');
    setAcknowledged(true);
  };

  return (
    <>
      {children}
      <CrisisAcknowledgement isOpen={isAuthenticated && !acknowledged} onConfirm={handleConfirm} />
    </>
  );
};

export { CRISIS_ACKNOWLEDGED_KEY, CrisisAcknowledgement, CrisisAcknowledgementGate };
